import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Hash, Users, RefreshCw, Search, Shield, User, Wrench, CreditCard } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import { Department } from './DepartmentManager';
import { toast } from 'sonner';
interface ChatMessage {
  id: string;
  channelId: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  content: string;
  timestamp: string;
}
export function StaffChat() {
  const {
    adminUser
  } = useAuth();
  const {
    staff
  } = useAppContext();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeChannel, setActiveChannel] = useState<string>('general');
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentStaff = staff.find(s => s.email === adminUser?.email) || {
    id: adminUser?.id || 'unknown',
    name: adminUser?.email?.split('@')[0] || 'Admin',
    role: 'admin'
  };
  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, activeChannel]);
  const loadData = async () => {
    try {
      setIsLoading(true);
      // Load departments
      const {
        data: deptData
      } = await supabase.from('site_settings').select('value').eq('key', 'departments').maybeSingle();
      if (deptData?.value) {
        setDepartments((deptData.value as Department[]).filter(d => d.isActive));
      }
      // Load messages
      const {
        data: msgData
      } = await supabase.from('site_settings').select('value').eq('key', 'staff_chat_messages').maybeSingle();
      if (msgData?.value) {
        setMessages(msgData.value as ChatMessage[]);
      }
    } catch (error) {
      console.error('Failed to load chat data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;
    const msg: ChatMessage = {
      id: `msg_${Date.now()}`,
      channelId: activeChannel,
      senderId: currentStaff.id,
      senderName: currentStaff.name,
      senderRole: currentStaff.role,
      content: newMessage.trim(),
      timestamp: new Date().toISOString()
    };
    const updatedMessages = [...messages, msg];
    // Optimistic update
    setMessages(updatedMessages);
    setNewMessage('');
    setIsSending(true);
    try {
      await supabase.from('site_settings').upsert({
        key: 'staff_chat_messages',
        value: updatedMessages
      }, {
        onConflict: 'key'
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Revert on failure
      setMessages(messages);
    } finally {
      setIsSending(false);
    }
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield size={12} className="text-red-500" data-id="element-1130" />;
      case 'support':
        return <User size={12} className="text-blue-500" data-id="element-1131" />;
      case 'technician':
        return <Wrench size={12} className="text-purple-500" data-id="element-1132" />;
      case 'billing':
        return <CreditCard size={12} className="text-emerald-500" data-id="element-1133" />;
      default:
        return <User size={12} className="text-slate-500" data-id="element-1134" />;
    }
  };
  const channels = [{
    id: 'general',
    name: 'General',
    icon: Hash
  }, ...departments.map(d => ({
    id: d.id,
    name: d.name,
    icon: Hash
  }))];
  const activeMessages = messages.filter(m => m.channelId === activeChannel);
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-1135">
        <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-1136" />
      </div>;
  }
  return <div className="h-[calc(100vh-120px)] flex flex-col bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-1137">
      {/* Header */}
      <div className="h-16 border-b border-slate-800 flex items-center justify-between px-6 bg-slate-950 shrink-0" data-id="element-1138">
        <div data-id="element-1139">
          <h1 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-1140">
            <MessageSquare size={20} className="text-fox-500" data-id="element-1141" />
            Staff Chat
          </h1>
        </div>
        <div className="flex items-center gap-4" data-id="element-1142">
          <div className="flex items-center gap-2 text-sm text-slate-400" data-id="element-1143">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1144" />
            {currentStaff.name} ({currentStaff.role})
          </div>
          <button onClick={loadData} className="p-2 text-slate-400 hover:text-white bg-slate-900 rounded-lg border border-slate-800 transition-colors" title="Refresh Messages" data-id="element-1145">
            <RefreshCw size={16} data-id="element-1146" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0" data-id="element-1147">
        {/* Sidebar */}
        <div className="w-64 border-r border-slate-800 bg-slate-950/50 flex flex-col shrink-0" data-id="element-1148">
          <div className="p-4" data-id="element-1149">
            <div className="relative" data-id="element-1150">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1151" />
              <input type="text" placeholder="Search channels..." className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-fox-500" data-id="element-1152" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-1153">
            <div className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1154">
              Channels
            </div>
            {channels.map(channel => {
            const isActive = activeChannel === channel.id;
            const unreadCount = 0; // In a real app, track last read timestamp per channel
            return <button key={channel.id} onClick={() => setActiveChannel(channel.id)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-fox-600/10 text-fox-500' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1155">
                  <div className="flex items-center gap-2 truncate" data-id="element-1156">
                    <channel.icon size={16} className={isActive ? 'text-fox-500' : 'text-slate-500'} data-id="element-1157" />
                    <span className="truncate" data-id="element-1158">{channel.name}</span>
                  </div>
                  {unreadCount > 0 && <span className="bg-fox-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-1159">
                      {unreadCount}
                    </span>}
                </button>;
          })}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 bg-slate-950" data-id="element-1160">
          {/* Channel Header */}
          <div className="h-14 border-b border-slate-800 flex items-center px-6 shrink-0" data-id="element-1161">
            <div className="flex items-center gap-2" data-id="element-1162">
              <Hash size={20} className="text-slate-500" data-id="element-1163" />
              <h2 className="font-bold text-white" data-id="element-1164">
                {channels.find(c => c.id === activeChannel)?.name}
              </h2>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1165">
            {activeMessages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-slate-500" data-id="element-1166">
                <MessageSquare size={48} className="mb-4 opacity-20" data-id="element-1167" />
                <p data-id="element-1168">No messages in this channel yet.</p>
                <p className="text-sm mt-1" data-id="element-1169">
                  Be the first to start the conversation!
                </p>
              </div> : activeMessages.map((msg, i) => {
            const isMe = msg.senderId === currentStaff.id;
            const showHeader = i === 0 || activeMessages[i - 1].senderId !== msg.senderId || new Date(msg.timestamp).getTime() - new Date(activeMessages[i - 1].timestamp).getTime() > 5 * 60000;
            return <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`} data-id="element-1170">
                    {showHeader && <div className={`flex items-center gap-2 mb-1 ${isMe ? 'flex-row-reverse' : ''}`} data-id="element-1171">
                        <span className="text-sm font-medium text-slate-300" data-id="element-1172">
                          {msg.senderName}
                        </span>
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded" data-id="element-1173">
                          {getRoleIcon(msg.senderRole)}
                          {msg.senderRole}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-1174">
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                        </span>
                      </div>}
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${isMe ? 'bg-fox-600 text-white rounded-tr-sm' : 'bg-slate-800 text-slate-200 rounded-tl-sm'}`} data-id="element-1175">
                      {msg.content}
                    </div>
                  </div>;
          })}
            <div ref={messagesEndRef} data-id="element-1176" />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-800 bg-slate-950 shrink-0" data-id="element-1177">
            <form onSubmit={handleSendMessage} className="flex gap-2" data-id="element-1178">
              <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder={`Message #${channels.find(c => c.id === activeChannel)?.name.toLowerCase()}...`} className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 transition-colors" data-id="element-1179" />
              <button type="submit" disabled={!newMessage.trim() || isSending} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shrink-0" data-id="element-1180">
                <Send size={18} className={isSending ? 'opacity-0' : 'opacity-100'} data-id="element-1181" />
                {isSending && <div className="absolute w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1182" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>;
}